import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";

export type StreamEventsHistoryRange = "5m" | "15m" | "1h" | "3h" | "6h" | "12h" | "24h" | "all";

type StreamEventsAnalyticsChartProps = {
  streamId: string;
  range: "latest" | StreamEventsHistoryRange;
  refreshMs: number;
};

type StreamErrorRateMeasurement = {
  mpegTsErrorRate?: number | null;
  klvErrorRate?: number | null;
};

type StreamErrorRatesResponse = {
  streamId?: string;
  errorRates?: StreamErrorRateMeasurement[];
  errorRateMeasurementsMax?: number;
  error?: string;
};

type StreamLastErrorRateResponse = {
  streamId?: string;
  errorRate?: StreamErrorRateMeasurement | null;
  error?: string;
};

type AnalyticsErrorRatePoint = {
  timestamp?: string;
  mpegTsRatePerSec?: number;
  klvRatePerSec?: number;
  mpegTsSampleCount?: number;
  klvSampleCount?: number;
};

type AnalyticsErrorRateResponse = {
  streamId?: string;
  bucket?: string;
  points?: AnalyticsErrorRatePoint[];
  error?: string;
  details?: string;
};

type ChartPoint = {
  time: string;
  mpegTs: number;
  klv: number;
  mpegTsSamples: number;
  klvSamples: number;
};

type LatestSeriesPoint = {
  mpegTs: number;
  klv: number;
};

type LatestFetchResult =
  | { type: "snapshot"; errorRates: LatestSeriesPoint[]; errorRateMeasurementsMax?: number; error?: string }
  | { type: "incremental"; errorRate: LatestSeriesPoint; error?: string };

const RANGE_TO_DURATION_MS: Record<StreamEventsHistoryRange, number | null> = {
  "5m": 5 * 60 * 1000,
  "15m": 15 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "3h": 3 * 60 * 60 * 1000,
  "6h": 6 * 60 * 60 * 1000,
  "12h": 12 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  all: null,
};
const LATEST_REFRESH_MS = 2000;
const MAX_CHART_POINTS = 300;
const DEFAULT_MAX_ERROR_RATE_POINTS = 100;

const pickBucketForRange = (range: StreamEventsHistoryRange) => {
  switch (range) {
    case "5m":
    case "15m":
      return "raw";
    case "1h":
    case "3h":
      return "5s";
    case "6h":
    case "12h":
    case "24h":
    case "all":
    default:
      return "1m";
  }
};

const toFiniteNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeHistoryPoint = (point: AnalyticsErrorRatePoint) => {
  const timestamp = point.timestamp ? new Date(point.timestamp) : null;
  return {
    timestamp,
    mpegTs: toFiniteNumber(point.mpegTsRatePerSec),
    klv: toFiniteNumber(point.klvRatePerSec),
    mpegTsSamples: Math.max(0, Math.trunc(toFiniteNumber(point.mpegTsSampleCount))),
    klvSamples: Math.max(0, Math.trunc(toFiniteNumber(point.klvSampleCount))),
  };
};

const downsampleData = (data: ChartPoint[]) => {
  if (data.length <= MAX_CHART_POINTS) {
    return data;
  }

  const groupSize = Math.ceil(data.length / MAX_CHART_POINTS);
  const reduced: ChartPoint[] = [];
  for (let i = 0; i < data.length; i += groupSize) {
    const slice = data.slice(i, i + groupSize);
    const mpegTsSamples = slice.reduce((sum, point) => sum + point.mpegTsSamples, 0);
    const klvSamples = slice.reduce((sum, point) => sum + point.klvSamples, 0);
    const last = slice[slice.length - 1];

    const mpegTs =
      mpegTsSamples > 0
        ? slice.reduce((sum, point) => sum + point.mpegTs * point.mpegTsSamples, 0) / mpegTsSamples
        : 0;
    const klv =
      klvSamples > 0
        ? slice.reduce((sum, point) => sum + point.klv * point.klvSamples, 0) / klvSamples
        : 0;

    reduced.push({
      time: last.time,
      mpegTs,
      klv,
      mpegTsSamples,
      klvSamples,
    });
  }
  return reduced;
};

const StreamEventsAnalyticsChart = ({ streamId, range, refreshMs }: StreamEventsAnalyticsChartProps) => {
  const { authorizedFetch } = useAuth();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [latestSeries, setLatestSeries] = useState<LatestSeriesPoint[]>([]);
  const [latestSeriesMax, setLatestSeriesMax] = useState<number>(DEFAULT_MAX_ERROR_RATE_POINTS);
  const [hasInitialLatestFetch, setHasInitialLatestFetch] = useState(false);

  const isLatestRange = range === "latest";
  const previousRangeRef = useRef<"latest" | StreamEventsHistoryRange>(range);
  const isEnteringLatestRange = isLatestRange && previousRangeRef.current !== "latest";

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width, height } = entry.contentRect;
      setContainerSize((current) =>
        current.width === width && current.height === height ? current : { width, height },
      );
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setLatestSeries([]);
    setLatestSeriesMax(DEFAULT_MAX_ERROR_RATE_POINTS);
    setHasInitialLatestFetch(false);
  }, [streamId]);

  useEffect(() => {
    if (isLatestRange) {
      setLatestSeries([]);
      setLatestSeriesMax(DEFAULT_MAX_ERROR_RATE_POINTS);
      setHasInitialLatestFetch(false);
    }
  }, [isLatestRange]);

  useEffect(() => {
    previousRangeRef.current = range;
  }, [range]);

  const clampLatestSeries = useCallback(
    (series: LatestSeriesPoint[]) => {
      const capacity = latestSeriesMax > 0 ? latestSeriesMax : DEFAULT_MAX_ERROR_RATE_POINTS;
      if (series.length <= capacity) {
        return series;
      }
      return series.slice(series.length - capacity);
    },
    [latestSeriesMax],
  );

  const latestRatesQuery = useQuery<LatestFetchResult>({
    queryKey: ["stream-events", "error-rates", "latest", streamId],
    enabled: Boolean(streamId) && isLatestRange,
    refetchInterval: LATEST_REFRESH_MS,
    refetchIntervalInBackground: true,
    structuralSharing: false,
    queryFn: async () => {
      if (isEnteringLatestRange || !hasInitialLatestFetch) {
        try {
          const response = await authorizedFetch(`/api/streams/${encodeURIComponent(streamId)}/error_rates`);
          const payload = (await response.json().catch(() => null)) as StreamErrorRatesResponse | null;
          if (!response.ok) {
            const message = payload?.error ?? "Unable to load stream error rates";
            return { type: "snapshot", errorRates: [], error: message } as const;
          }
          return {
            type: "snapshot",
            errorRates: (payload?.errorRates ?? []).map((entry) => ({
              mpegTs: toFiniteNumber(entry?.mpegTsErrorRate),
              klv: toFiniteNumber(entry?.klvErrorRate),
            })),
            errorRateMeasurementsMax: payload?.errorRateMeasurementsMax,
          } as const;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Unable to load stream error rates";
          return { type: "snapshot", errorRates: [], error: message } as const;
        }
      }

      try {
        const response = await authorizedFetch(`/api/streams/${encodeURIComponent(streamId)}/last_error_rate`);
        const payload = (await response.json().catch(() => null)) as StreamLastErrorRateResponse | null;
        if (!response.ok) {
          const message = payload?.error ?? "Unable to load stream error rate";
          return { type: "incremental", errorRate: { mpegTs: 0, klv: 0 }, error: message } as const;
        }
        return {
          type: "incremental",
          errorRate: {
            mpegTs: toFiniteNumber(payload?.errorRate?.mpegTsErrorRate),
            klv: toFiniteNumber(payload?.errorRate?.klvErrorRate),
          },
        } as const;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to load stream error rate";
        return { type: "incremental", errorRate: { mpegTs: 0, klv: 0 }, error: message } as const;
      }
    },
  });

  useEffect(() => {
    const result = latestRatesQuery.data;
    if (!isLatestRange || !result) {
      return;
    }

    if (result.type === "snapshot") {
      setHasInitialLatestFetch(true);
      const maxFromServer =
        typeof result.errorRateMeasurementsMax === "number" && result.errorRateMeasurementsMax > 0
          ? result.errorRateMeasurementsMax
          : undefined;
      const capacity = maxFromServer ?? latestSeriesMax ?? DEFAULT_MAX_ERROR_RATE_POINTS;
      if (maxFromServer) {
        setLatestSeriesMax(maxFromServer);
      }
      setLatestSeries(() => {
        const incoming = (result.errorRates ?? []).map((entry) => ({
          mpegTs: toFiniteNumber(entry.mpegTs),
          klv: toFiniteNumber(entry.klv),
        }));
        const series = incoming.length > 0 ? incoming : [{ mpegTs: 0, klv: 0 }];
        if (series.length <= capacity) {
          return series;
        }
        return series.slice(series.length - capacity);
      });
      return;
    }

    setHasInitialLatestFetch(true);
    setLatestSeries((current) => clampLatestSeries([...current, result.errorRate]));
  }, [clampLatestSeries, isLatestRange, latestRatesQuery.data, latestSeriesMax]);

  const historyQuery = useQuery<AnalyticsErrorRateResponse, Error>({
    queryKey: ["analytics-stream-error-rates", streamId, range],
    enabled: Boolean(streamId) && !isLatestRange,
    refetchInterval: refreshMs,
    refetchIntervalInBackground: true,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      if (isLatestRange) {
        return {};
      }
      const end = new Date();
      const duration = RANGE_TO_DURATION_MS[range];
      const start = duration === null ? new Date(0) : new Date(end.getTime() - duration);
      const params = new URLSearchParams();
      params.set("end", end.toISOString());
      params.set("start", start.toISOString());
      params.set("bucket", pickBucketForRange(range));
      const response = await authorizedFetch(
        `/api/analytics/streams/${encodeURIComponent(streamId)}/error_rates?${params.toString()}`,
      );
      const payload = (await response.json().catch(() => null)) as AnalyticsErrorRateResponse | null;
      if (!response.ok) {
        const baseError = payload?.error ?? "Unable to load stream error-rate analytics data";
        const details = typeof payload?.details === "string" ? payload.details.trim() : "";
        throw new Error(details ? `${baseError}: ${details}` : baseError);
      }
      return payload ?? {};
    },
  });

  const chartData: ChartPoint[] = useMemo(() => {
    if (isLatestRange) {
      if (latestSeries.length === 0) {
        return [];
      }
      const formatter = new Intl.DateTimeFormat(undefined, {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      const now = Date.now();
      return latestSeries.map((sample, idx) => {
        const timestamp = new Date(now - (latestSeries.length - 1 - idx) * LATEST_REFRESH_MS);
        return {
          time: formatter.format(timestamp),
          mpegTs: sample.mpegTs,
          klv: sample.klv,
          mpegTsSamples: 1,
          klvSamples: 1,
        };
      });
    }

    const bucket = historyQuery.data?.bucket ?? "raw";
    const bucketDurationMs = bucket === "1m" ? 60_000 : bucket === "5s" ? 5_000 : 1_000;
    const duration = RANGE_TO_DURATION_MS[range];
    const nowMs = Date.now();

    const normalizedPoints = (historyQuery.data?.points ?? [])
      .map(normalizeHistoryPoint)
      .filter((point) => point.timestamp instanceof Date && !Number.isNaN(point.timestamp.valueOf()))
      .sort((a, b) => (a.timestamp!.getTime() > b.timestamp!.getTime() ? 1 : -1));

    if (normalizedPoints.length === 0) {
      return [];
    }

    const windowEnd = duration === null ? normalizedPoints[normalizedPoints.length - 1].timestamp!.getTime() : nowMs;
    const windowStart = duration === null ? normalizedPoints[0].timestamp!.getTime() : windowEnd - duration;

    const filteredPoints = normalizedPoints.filter((point) => {
      const ts = point.timestamp!.getTime();
      return ts >= windowStart && ts <= windowEnd;
    });

    if (filteredPoints.length === 0) {
      return [];
    }

    const showDate = duration === null || duration >= 12 * 60 * 60 * 1000;
    const formatter = new Intl.DateTimeFormat(undefined, {
      hour12: false,
      ...(showDate ? { month: "short", day: "numeric" } : {}),
      hour: "2-digit",
      minute: "2-digit",
    });

    const startMs = Math.floor(windowStart / bucketDurationMs) * bucketDurationMs;
    const endMs = Math.max(windowEnd, filteredPoints[filteredPoints.length - 1].timestamp!.getTime());

    const bucketMap = new Map<number, { mpegTs: number; klv: number; mpegTsSamples: number; klvSamples: number }>();
    filteredPoints.forEach((point) => {
      const bucketKey = Math.floor(point.timestamp!.getTime() / bucketDurationMs) * bucketDurationMs;
      bucketMap.set(bucketKey, {
        mpegTs: point.mpegTs,
        klv: point.klv,
        mpegTsSamples: point.mpegTsSamples,
        klvSamples: point.klvSamples,
      });
    });

    const data: ChartPoint[] = [];
    for (let ts = startMs; ts <= endMs; ts += bucketDurationMs) {
      const value = bucketMap.get(ts) ?? { mpegTs: 0, klv: 0, mpegTsSamples: 0, klvSamples: 0 };
      data.push({
        time: formatter.format(new Date(ts)),
        mpegTs: value.mpegTs,
        klv: value.klv,
        mpegTsSamples: value.mpegTsSamples,
        klvSamples: value.klvSamples,
      });
    }

    return downsampleData(data);
  }, [historyQuery.data?.bucket, historyQuery.data?.points, isLatestRange, latestSeries, range]);

  const maxValue = useMemo(() => {
    if (!chartData.length) {
      return 1;
    }
    const maxMpegTs = chartData.reduce((max, point) => (point.mpegTs > max ? point.mpegTs : max), 0);
    const maxKlv = chartData.reduce((max, point) => (point.klv > max ? point.klv : max), 0);
    const max = Math.max(maxMpegTs, maxKlv);
    return max > 0 ? max * 1.1 : 1;
  }, [chartData]);

  const chartBackgroundColor = theme.palette.background.paper;
  const chartBorderColor = theme.palette.divider;
  const isCompact = useMemo(
    () => containerSize.width < 320 || containerSize.height < 120,
    [containerSize.height, containerSize.width],
  );

  const renderTooltip = useCallback(
    (props: TooltipProps<any, any>) => {
      const { active, payload, label } = props as any;
      if (!active || !payload || payload.length === 0) {
        return null;
      }
      const point = payload[0]?.payload as ChartPoint | undefined;
      if (!point) {
        return null;
      }

      return (
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 3,
            p: 1,
            border: `1px solid ${theme.palette.divider}`,
            minWidth: 180,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Error rate: at {label}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "error.main" }}>
            MPEG-TS: {point.mpegTs.toFixed(2)} /s
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "info.main" }}>
            KLV: {point.klv.toFixed(2)} /s
          </Typography>
        </Box>
      );
    },
    [theme],
  );

  if (!streamId) {
    return null;
  }

  const query = isLatestRange ? latestRatesQuery : historyQuery;
  const emptyLabel = isLatestRange ? "No stream error data yet." : "No stream error data for this range.";

  if (query.isPending && chartData.length === 0) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: chartBackgroundColor,
          border: `1px solid ${chartBorderColor}`,
          borderRadius: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (query.isError && chartData.length === 0) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: chartBackgroundColor,
          border: `1px solid ${chartBorderColor}`,
          borderRadius: 2,
          px: 2,
        }}
      >
        <Typography variant="body2" color="error">
          {(query.error as Error)?.message ?? "Unable to load stream error data"}
        </Typography>
      </Box>
    );
  }

  if (!chartData.length) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: chartBackgroundColor,
          border: `1px solid ${chartBorderColor}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: chartBackgroundColor,
        border: `1px solid ${chartBorderColor}`,
        borderRadius: 2,
        pt: 1,
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={
            isCompact
              ? { top: 8, right: 6, left: -20, bottom: 0 }
              : { top: 10, right: 16, left: -20, bottom: 6 }
          }
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} stroke={theme.palette.divider} />
          <XAxis dataKey="time" tick={{ fontSize: 9, fill: theme.palette.text.secondary }} label={{ fontSize: 10 }} />
          <YAxis
            domain={[0, maxValue]}
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            label={isCompact ? undefined : { value: "errors/s", angle: -90, position: "insideLeft", fontSize: 10, offset: 10 }}
          />
          <defs>
            <linearGradient id="eventsMpegTsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.palette.error.main} stopOpacity={0.32} />
              <stop offset="100%" stopColor={theme.palette.error.main} stopOpacity={0.04} />
            </linearGradient>
            <linearGradient id="eventsKlvGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.palette.info.main} stopOpacity={0.32} />
              <stop offset="100%" stopColor={theme.palette.info.main} stopOpacity={0.04} />
            </linearGradient>
          </defs>
          <RechartsTooltip content={renderTooltip} />
          <Area
            type="monotone"
            dataKey="mpegTs"
            stroke={theme.palette.error.main}
            fill="url(#eventsMpegTsGradient)"
            fillOpacity={1}
            strokeWidth={2}
            isAnimationActive={true}
            name="mpegTs"
          />
          <Area
            type="monotone"
            dataKey="klv"
            stroke={theme.palette.info.main}
            fill="url(#eventsKlvGradient)"
            fillOpacity={1}
            strokeWidth={2}
            isAnimationActive={true}
            name="klv"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default StreamEventsAnalyticsChart;
